/**
 * Unified Authentication Bridge
 * 
 * Manages authentication across all supported platforms (Farcaster, World App, Base, Web)
 * using a standardized Adapter pattern.
 * 
 * CRITICAL FIX (Jan 2026): 
 * - Dynamic SDK loading to prevent import errors
 * - Proper Farcaster Frames v2 API usage (no sdk.init())
 * - Context-based auth fallback when signIn() unavailable
 * 
 * @module engine/auth/authBridge
 */

import { logger } from '../../utils/logger.js';
import { detectPlatform, PLATFORM, forcePlatformRedetection } from '../device.js';
import getBaseMiniKitClient from '../wallet/baseMiniKit';

// Dynamic SDK imports - only load when needed
let farcasterSdk = null;
let worldMiniKit = null;

async function getFarcasterSdk() {
    if (!farcasterSdk) {
        try {
            const module = await import('@farcaster/miniapp-sdk');
            farcasterSdk = module.default || module;
            console.log('[AuthBridge] Farcaster SDK loaded:', Object.keys(farcasterSdk));
            
            // CRITICAL: Call ready() IMMEDIATELY after loading to prevent reload/black screen
            if (farcasterSdk?.actions?.ready) {
                farcasterSdk.actions.ready();
                console.log('[AuthBridge] 🚀 sdk.actions.ready() called IMMEDIATELY');
            }
        } catch (e) {
            console.warn('[AuthBridge] Failed to load Farcaster SDK:', e.message);
            return null;
        }
    }
    return farcasterSdk;
}

async function getWorldMiniKit() {
    console.log('[AuthBridge] 🌍 getWorldMiniKit() called, cached:', !!worldMiniKit);
    if (!worldMiniKit) {
        try {
            console.log('[AuthBridge] 🌍 Attempting to import @worldcoin/minikit-js...');
            const module = await import('@worldcoin/minikit-js');
            console.log('[AuthBridge] 🌍 Module imported:', {
                hasMiniKit: !!module.MiniKit,
                moduleKeys: Object.keys(module)
            });
            worldMiniKit = module.MiniKit;
            console.log('[AuthBridge] 🌍✅ World MiniKit loaded successfully');
        } catch (e) {
            console.error('[AuthBridge] 🌍❌ Failed to load World MiniKit:', e);
            return null;
        }
    }
    console.log('[AuthBridge] 🌍 Returning MiniKit:', {
        exists: !!worldMiniKit,
        isInstalled: worldMiniKit ? worldMiniKit.isInstalled() : false
    });
    return worldMiniKit;
}

// ---------------------------------------------------------------------------
// Internal State
// ---------------------------------------------------------------------------

let initialized = false;
let currentPlatform = null;
let authState = {
    user: null,
    context: null,
    provider: null
};

const listeners = {
    auth: new Set(),
    error: new Set(),
    context: new Set()
};

function emit(event, payload) {
    listeners[event]?.forEach(cb => {
        try { cb(payload); } catch (e) { console.error(e); }
    });
}

// ---------------------------------------------------------------------------
// Adapters
// ---------------------------------------------------------------------------

/**
 * Farcaster Adapter
 * Uses @farcaster/miniapp-sdk with Frames v2 API
 * CRITICAL: No sdk.init() - modern SDK doesn't use it
 */
const FarcasterAdapter = {
    sdk: null,
    
    init: async () => {
        // NOTE: getFarcasterSdk() already calls ready() immediately after loading
        const sdk = await getFarcasterSdk();
        if (!sdk) {
            console.warn('[Auth] Farcaster SDK not available');
            return;
        }
        FarcasterAdapter.sdk = sdk;
        
        // Fetch initial context (Frames v2 API)
        // NOTE: ready() was already called in getFarcasterSdk(), so this is safe
        try {
            // FIXED: Context is synchronous in 2025 SDK, not a Promise
            const ctx = sdk.context;
            if (ctx) {
                authState.context = ctx;
                emit('context', ctx);
                console.log('[Auth] Farcaster context:', {
                    fid: ctx?.user?.fid,
                    username: ctx?.user?.username,
                    address: ctx?.user?.custodyAddress
                });
            } else {
                console.log('[Auth] No initial Farcaster context available');
            }
        } catch (e) {
            console.log('[Auth] Error accessing Farcaster context:', e.message);
        }

        // Set up context listener if available
        if (typeof sdk.onContextUpdate === 'function') {
            sdk.onContextUpdate(ctx => {
                authState.context = ctx;
                emit('context', ctx);
            });
        }
        
        // ready() was already called in getFarcasterSdk() - no need to call again
    },

    login: async () => {
        console.log('[Auth] FarcasterAdapter.login() called', {
            hasSdk: !!FarcasterAdapter.sdk,
            hasContext: !!authState.context,
            contextUser: authState.context?.user?.username
        });
        
        const sdk = FarcasterAdapter.sdk || await getFarcasterSdk();
        if (!sdk) {
            console.error('[Auth] Farcaster SDK not available after load attempt');
            throw new Error('Farcaster SDK not available');
        }
        // Quick Auth gives the backend a signed, verifiable Farcaster identity.
        let platformToken = null;
        try {
            if (sdk.quickAuth?.getToken) {
                const quickAuthResult = await sdk.quickAuth.getToken();
                platformToken = quickAuthResult?.token || null;
                console.log('[Auth] QuickAuth token acquired:', !!platformToken);
            }
        } catch (quickAuthError) {
            console.warn('[Auth] QuickAuth getToken failed:', quickAuthError?.message);
        }

        // Helper to extract address from context (handles Proxies/Promises)
        const getAddressFromContext = async (ctx) => {
            if (!ctx?.user) return null;
            
            // Try multiple sources for the address
            // CRITICAL FIX: Farcaster SDK returns Proxy(Function) that must be awaited
            let address = ctx.user.custodyAddress || 
                         ctx.user.custody_address ||
                         (ctx.user.verifications && ctx.user.verifications[0]) ||
                         ctx.user.address;
            
            // If address is a Promise/function, await it
            if (typeof address === 'function') {
                try {
                    address = await address();
                } catch (e) {
                    console.warn('[Auth] Failed to resolve address function:', e);
                }
            }
            
            // If it's still a Promise, await it
            if (address instanceof Promise) {
                try {
                    address = await address;
                } catch (e) {
                    console.warn('[Auth] Failed to resolve address promise:', e);
                }
            }
            
            return address;
        };

        // STRATEGY 1: Check if we have context with address (MOBILE ONLY)
        // CRITICAL FIX: Skip STRATEGY 1 on desktop because Farcaster SDK blocks Proxy access
        // Desktop error: "Invalid path: context/user/custodyAddress. Method: APPLY"
        const isMobile = /iPhone|iPad|Android/i.test(navigator.userAgent);
        
        if (isMobile && authState.context?.user) {
            console.log('[Auth] STRATEGY 1 (Mobile): Checking existing context...');
            const user = authState.context.user;
            // CRITICAL FIX: await the address extraction (handles Proxy/Promise)
            const address = await getAddressFromContext(authState.context);
            
            // Resolve fid and username if they're also Proxies/Promises
            let fid = user.fid;
            let username = user.username;
            
            if (typeof fid === 'function') fid = await fid().catch(() => null);
            if (fid instanceof Promise) fid = await fid.catch(() => null);
            
            if (typeof username === 'function') username = await username().catch(() => null);
            if (username instanceof Promise) username = await username.catch(() => null);
            
            console.log('[Auth] Context user details:', {
                fid,
                username,
                extractedAddress: address
            });
            
            if (address) {
                console.log('[Auth] ✅ Context auth success (Mobile):', { fid, address });
                return {
                    platformToken,
                    platform: PLATFORM.FARCASTER,
                    address: address,
                    username: username || address,
                    fid: fid,
                    signature: null,
                    message: null,
                    context: authState.context,
                    authType: 'context'
                };
            }
        } else if (!isMobile && authState.context?.user) {
            console.log('[Auth] STRATEGY 1 SKIPPED (Desktop): Farcaster SDK blocks Proxy access, using eth_requestAccounts instead');
        }

        // STRATEGY 2: Use sdk.wallet.ethProvider to get address
        if (sdk.wallet?.ethProvider) {
            try {
                console.log('[Auth] STRATEGY 2: Getting address from sdk.wallet.ethProvider...');
                const accounts = await sdk.wallet.ethProvider.request({ 
                    method: 'eth_requestAccounts' 
                });
                if (accounts && accounts.length > 0) {
                    const address = accounts[0];
                    console.log('[Auth] ✅ ethProvider address obtained:', address);
                    return {
                        platformToken,
                        platform: PLATFORM.FARCASTER,
                        address: address,
                        username: authState.context?.user?.username || 'Farcaster User',
                        fid: authState.context?.user?.fid,
                        signature: null,
                        message: null,
                        context: authState.context,
                        authType: 'wallet'
                    };
                }
            } catch (e) {
                console.warn('[Auth] sdk.wallet.ethProvider failed:', e.message);
            }
        }

        // STRATEGY 3: Check window.ethereum (Wagmi may have connected it)
        if (typeof window !== 'undefined' && window.ethereum) {
            try {
                console.log('[Auth] STRATEGY 3: Checking window.ethereum for connected wallet...');
                // First check if already connected (don't prompt)
                const accounts = await window.ethereum.request({ 
                    method: 'eth_accounts'  // Note: eth_accounts doesn't prompt
                });
                if (accounts && accounts.length > 0) {
                    const address = accounts[0];
                    console.log('[Auth] ✅ Found connected wallet via window.ethereum:', address);
                    return {
                        platformToken,
                        platform: PLATFORM.FARCASTER,
                        address: address,
                        username: authState.context?.user?.username || 'Farcaster User',
                        fid: authState.context?.user?.fid,
                        signature: null,
                        message: null,
                        context: authState.context,
                        authType: 'ethereum'
                    };
                }
            } catch (e) {
                console.warn('[Auth] window.ethereum check failed:', e.message);
            }
        }

        // STRATEGY 4: Return context with FID even without address
        // The backend can handle context-based auth with just FID
        if (authState.context?.user?.fid) {
            console.log('[Auth] STRATEGY 4: Using FID-only auth (no address available)');
            return {
                platformToken,
                platform: PLATFORM.FARCASTER,
                address: null, // No address, but we have FID
                username: authState.context.user.username,
                fid: authState.context.user.fid,
                signature: null,
                message: null,
                context: authState.context,
                authType: 'fid-only'
            };
        }

        // DO NOT call signIn() - it's buggy on Warpcast Desktop
        console.error('[Auth] All Farcaster auth strategies failed - no address or FID available');
        throw new Error('Farcaster auth failed - no context or wallet available');
    }
};

/**
 * World App Adapter
 * Uses @worldcoin/minikit-js v2.0+ for Wallet Auth
 * CRITICAL: Dynamic loading to prevent import errors
 */
const WorldAdapter = {
    MiniKit: null,
    
    init: async () => {
        const MiniKit = await getWorldMiniKit();
        if (!MiniKit) {
            console.warn('[Auth] World MiniKit not available');
            return;
        }
        WorldAdapter.MiniKit = MiniKit;
        
        try {
            if (!MiniKit.isInstalled()) {
                console.warn('[Auth] World MiniKit not installed/detected - attempting install');
                // CRITICAL FIX: MiniKit.install() requires appId parameter
                let appId = import.meta.env.VITE_MINIKIT_PROJECT_ID || import.meta.env.VITE_WC_PROJECT_ID;
                if (!appId || appId === '***************') {
                    console.error('[Auth] VITE_MINIKIT_PROJECT_ID not configured, using fallback');
                    appId = 'app_staging_92c30089e8751fa049b1093121516e45'; // Generic fallback to prevent WorldApp native crash
                }
                
                // EXTRA SAFEGUARD: Make sure appId is definitely a string
                appId = String(appId);
                
                console.log('[Auth] Installing MiniKit with appId:', appId?.substring(0, 10) + '...');
                // MiniKit v2 install() is async in some versions
                try {
                    await MiniKit.install(appId);
                } catch (installError) {
                    // Fallback for sync version
                    try {
                        MiniKit.install(appId);
                    } catch (syncError) {
                        console.error('[Auth] Sync MiniKit.install also failed:', syncError);
                    }
                }
                console.log('[Auth] World MiniKit install attempt completed');
            } else {
                console.log('[Auth] World MiniKit already installed');
            }
        } catch (e) {
            console.error('[Auth] World MiniKit init failed', e);
        }
    },

    login: async () => {
        console.log('[Auth] 🌍 WorldAdapter.login() called');
        const MiniKit = WorldAdapter.MiniKit || await getWorldMiniKit();
        if (!MiniKit) {
            console.error('[Auth] 🌍❌ World MiniKit not available during login attempt');
            throw new Error('World MiniKit not available');
        }
        
        console.log('[Auth] 🌍 Checking if MiniKit is installed...');
        if (!MiniKit.isInstalled()) {
            console.error('[Auth] 🌍❌ World MiniKit not installed during login attempt');
            throw new Error('World MiniKit not installed');
        }
        console.log('[Auth] 🌍✅ MiniKit is installed');

        console.log('[Auth] 🌍 Checking if commandsAsync.walletAuth exists...');
        if (!MiniKit.commandsAsync?.walletAuth) {
            console.error('[Auth] 🌍❌ World MiniKit commandsAsync.walletAuth not available');
            
            // FALLBACK TO SYNC COMMANDS FOR OLDER SDK VERSIONS
            if (MiniKit.commands?.walletAuth) {
                console.log('[Auth] 🌍 Using fallback sync commands.walletAuth');
                const nonce = crypto.randomUUID();
                const statement = 'Login to Dope Chain Wars';
                
                const finalPayload = await new Promise((resolve) => {
                    MiniKit.commands.walletAuth({
                        nonce,
                        expirationTime: new Date(Date.now() + 10 * 60 * 1000),
                        notBefore: new Date(),
                        statement
                    }, (payload) => {
                        resolve(payload);
                    });
                });
                
                if (!finalPayload || finalPayload.status === 'error') {
                    console.error('[Auth] 🌍❌ World App Login Failed (sync) - finalPayload error:', finalPayload);
                    throw new Error('World App Login Failed');
                }
                
                return {
                    platform: PLATFORM.WORLDCOIN,
                    address: finalPayload.address,
                    signature: finalPayload.signature,
                    message: finalPayload.message,
                    context: { isVerified: false }
                };
            }
            
            throw new Error('World MiniKit commandsAsync.walletAuth not available');
        }
        console.log('[Auth] 🌍✅ commandsAsync.walletAuth exists');

        console.log('[Auth] 🌍 Calling World MiniKit commandsAsync.walletAuth...');
        const nonce = crypto.randomUUID();
        const expirationTime = new Date(Date.now() + 10 * 60 * 1000);
        const notBefore = new Date();
        const statement = 'Login to Dope Chain Wars';
        
        console.log('[Auth] 🌍 Auth params:', { nonce, expirationTime, notBefore, statement });
        
        // CRITICAL FIX: Use commandsAsync (v2 API), not commands
        try {
            const payload = await MiniKit.commandsAsync.walletAuth({
                nonce,
                expirationTime,
                notBefore,
                statement
            });
            
            // If the v2 library returns { finalPayload } inside payload
            const actualPayload = payload.finalPayload || payload;

            console.log('[Auth] 🌍 World MiniKit walletAuth actualPayload:', actualPayload);

            if (!actualPayload || actualPayload.status === 'error') {
                console.error('[Auth] 🌍❌ World App Login Failed - payload error:', actualPayload);
                throw new Error('World App Login Failed');
            }

            console.log('[Auth] 🌍✅ World App Login Success');
            return {
                platform: PLATFORM.WORLDCOIN,
                address: actualPayload.address,
                signature: actualPayload.signature,
                message: actualPayload.message || 'context-auth',
                context: { isVerified: false }
            };
        } catch (error) {
            console.error('[Auth] 🌍❌ commandsAsync.walletAuth threw error:', error);
            throw error;
        }
    }
};

/**
 * Base App Adapter
 * Uses Base MiniKit via OnchainKit (window.miniKit with isFrameContext=false)
 * Also supports direct window.ethereum for Base-injected wallets
 * 
 * CRITICAL FIX (Jan 2026): Base App often ONLY injects window.ethereum
 * without window.miniKit, so we prioritize ethereum detection
 */
const BaseAdapter = {
    client: null,

    init: async () => {
        console.log('[Auth] BaseAdapter.init() called', {
            hasMiniKit: typeof window !== 'undefined' && !!window.miniKit,
            hasEthereum: typeof window !== 'undefined' && !!window.ethereum,
            isBase: typeof window !== 'undefined' && !!window.ethereum?.isBase
        });
        
        try {
            BaseAdapter.client = getBaseMiniKitClient();
            const success = await BaseAdapter.client.initialize();
            if (success) {
                console.log('[Auth] Base MiniKit initialized');
            } else {
                console.log('[Auth] Base MiniKit init returned false, will use ethereum fallback');
            }
        } catch (e) {
            console.warn('[Auth] Base MiniKit init failed:', e.message);
        }
    },

    login: async () => {
        console.log('[Auth] BaseAdapter.login() called', {
            clientAvailable: BaseAdapter.client?.isAvailable,
            hasEthereum: typeof window !== 'undefined' && !!window.ethereum
        });
        
        // STRATEGY 1: Try Base MiniKit client (if window.miniKit exists)
        if (!BaseAdapter.client) {
            BaseAdapter.client = getBaseMiniKitClient();
        }

        if (BaseAdapter.client.isAvailable) {
            try {
                console.log('[Auth] Trying Base MiniKit client...');
        const address = await BaseAdapter.client.connect();
                if (address) {
                    console.log('[Auth] ✅ Base MiniKit connect success:', address);
        return {
            platform: PLATFORM.BASE_MINIKIT,
            address: address,
            username: BaseAdapter.client.context?.user?.username || 'Base User',
            context: BaseAdapter.client.context
        };
                }
            } catch (e) {
                console.warn('[Auth] Base MiniKit connect failed:', e.message);
            }
        }

        // STRATEGY 2: Direct window.ethereum (Base often ONLY injects this)
        if (typeof window !== 'undefined' && window.ethereum) {
            console.log('[Auth] Trying Base via window.ethereum (primary for Base App)');
            try {
                // Check if it's actually a Base-related wallet
                const isBaseWallet = (
                    window.ethereum.isBase ||
                    window.ethereum.isBaseMiniKit ||
                    window.ethereum.isCoinbaseWallet
                );
                console.log('[Auth] ethereum wallet info:', { isBaseWallet, isCoinbase: window.ethereum.isCoinbaseWallet });
                
                const accounts = await window.ethereum.request({ 
                    method: 'eth_requestAccounts' 
                });
                
                if (accounts && accounts.length > 0) {
                    const address = accounts[0];
                    console.log('[Auth] ✅ Base ethereum connect success:', address);
                    
                    // CRITICAL FIX: Base App REQUIRES SIWE signature, not context-auth
                    // Request nonce+message from backend (backend stores exact message in Redis)
                    try {
                        const { getNonce } = await import('./authService.js');
                        console.log('[Auth] 🔍 Base: Fetching nonce for address:', address);
                        const nonceData = await getNonce(address);
                        console.log('[Auth] 🔍 Base: Nonce data received:', {
                            hasNonce: !!nonceData.nonce,
                            hasMessage: !!nonceData.message,
                            messagePreview: nonceData.message ? nonceData.message.substring(0, 100) : 'NONE'
                        });
                        
                        // CRITICAL: Use backend's EXACT message (already stored in Redis for verification)
                        const message = nonceData.message || nonceData.msg;
                        if (!message) {
                            console.error('[Auth] ❌ Base: No message in nonce response!', nonceData);
                            throw new Error('Backend did not return SIWE message in nonce response');
                        }
                        
                        console.log('[Auth] 🔐 Base: Requesting personal_sign with message length:', message.length);
                        // Sign the EXACT message from backend
                        const signature = await window.ethereum.request({
                            method: 'personal_sign',
                            params: [message, address]
                        });
                        
                        console.log('[Auth] ✅ Base: SIWE signature obtained, length:', signature.length);
                        const authPayload = {
                            platform: PLATFORM.BASE_MINIKIT,
                            address: address,
                            username: 'Base User',
                            signature: signature,
                            message: message, // Use backend's exact message
                            context: { source: 'ethereum_direct', isBaseWallet }
                        };
                        console.log('[Auth] 🎯 Base: Returning auth payload:', {
                            platform: authPayload.platform,
                            address: authPayload.address.substring(0, 10) + '...',
                            hasSignature: !!authPayload.signature,
                            hasMessage: !!authPayload.message,
                            messageLength: authPayload.message.length
                        });
                        return authPayload;
                    } catch (sigError) {
                        console.error('[Auth] ❌ Base SIWE signing failed:', sigError);
                        // User rejected signature - throw error
                        throw new Error('User rejected signature or signing failed', { cause: sigError });
                    }
                }
            } catch (e) {
                console.warn('[Auth] Base ethereum connect failed:', e.message);
                // If user rejected, don't throw - let UI show error
                if (e.code === 4001) {
                    throw new Error('User rejected wallet connection', { cause: e });
                }
            }
        }

        throw new Error('Base connection failed - no MiniKit or ethereum provider available');
    }
};

/**
 * Web Adapter
 * Uses Standard EIP-1193 Provider (MetaMask, WalletConnect via RainbowKit)
 * NOTE: For desktop/mobile web, RainbowKit handles the UI modal
 */
const WebAdapter = {
    init: async () => {
        // No specific init for browser wallets, they are injected
    },

    login: async (provider = window.ethereum) => {
        if (!provider) throw new Error('No Wallet Provider Found');

        // Request Accounts via EIP-1193
        const accounts = await provider.request({ method: 'eth_requestAccounts' });
        const address = accounts[0];

        return {
            platform: PLATFORM.DESKTOP,
            address,
            provider
        };
    }
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export async function initAuthBridge() {
    if (initialized) return;

    // Force fresh detection on init
    currentPlatform = forcePlatformRedetection();
    logger.info(`[AuthBridge] Initializing for ${currentPlatform}`);

    try {
        switch (currentPlatform) {
            case PLATFORM.FARCASTER:
                await FarcasterAdapter.init();
                break;
            case PLATFORM.WORLDCOIN:
                await WorldAdapter.init();
                break;
            case PLATFORM.BASE_MINIKIT:
                await BaseAdapter.init();
                break;
            case PLATFORM.DESKTOP:
            case PLATFORM.MOBILE:
            case PLATFORM.COINBASE:
            default:
                await WebAdapter.init();
                break;
        }
        initialized = true;
    } catch (err) {
        logger.error('[AuthBridge] Init Failed', err);
    }
}

export async function authenticate(options = {}) {
    if (!initialized) await initAuthBridge();

    // Allow override of platform for re-authentication scenarios
    const platform = options.platform || currentPlatform;
    logger.info(`[AuthBridge] Authenticating for ${platform}`);

    try {
        let result;
        switch (platform) {
            case PLATFORM.FARCASTER:
            case 'farcaster':
                result = await FarcasterAdapter.login();
                break;
            case PLATFORM.WORLDCOIN:
            case 'worldcoin':
            case 'world':
                result = await WorldAdapter.login();
                break;
            case PLATFORM.BASE_MINIKIT:
            case 'base_minikit':
            case 'base':
                result = await BaseAdapter.login();
                break;
            case PLATFORM.DESKTOP:
            case PLATFORM.MOBILE:
            case PLATFORM.COINBASE:
            case 'web':
            default:
                // For web/desktop, return null to signal UI should use RainbowKit modal
                // Direct eth_requestAccounts bypasses the modal UX
                result = null;
                break;
        }

        if (result) {
            emit('auth', result);
        }
        return result;

    } catch (err) {
        logger.error('[AuthBridge] Auth Failed', err);
        emit('error', err);
        throw err;
    }
}

// ---------------------------------------------------------------------------
// Exports & Accessors
// ---------------------------------------------------------------------------

export function getPlatform() {
    return currentPlatform || detectPlatform();
}

export function onAuth(cb) { listeners.auth.add(cb); return () => listeners.auth.delete(cb); }
export function onError(cb) { listeners.error.add(cb); return () => listeners.error.delete(cb); }
export function onContext(cb) { listeners.context.add(cb); return () => listeners.context.delete(cb); }

// Re-export for external usage
export { forcePlatformRedetection };

// Auto-init
if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
    });
    window.addEventListener('error', (event) => {
    });
    setTimeout(() => initAuthBridge(), 100);
}
